import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { useTheme, alpha } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import axios from '../utils/axiosConfig';
import {
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Chip,
  Box,
  Typography,
  Dialog,
  FormHelperText,
  Paper,
  Grid,
  Stack,
  Divider,
  Checkbox,
  FormGroup,
  IconButton
} from '@mui/material';
import { AddCircleOutline, DeleteOutline } from '@mui/icons-material';
import { CalendarMonth as CalendarIcon } from '@mui/icons-material';

// Add event form component
const EventForm = ({ event, onSave, onCancel, onDelete, calendars }) => {
  const theme = useTheme();
  
  // Format time for input field (HH:MM)
  const formatTimeForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Get default times
  const getDefaultStartTime = (sourceEvent = event) => {
    if (sourceEvent?.start) return formatTimeForInput(sourceEvent.start);
    return '09:00'; // Default to 9 AM
  };

  const getDefaultEndTime = (sourceEvent = event) => {
    if (sourceEvent?.end) return formatTimeForInput(sourceEvent.end);
    if (sourceEvent?.start) {
      const end = new Date(sourceEvent.start);
      end.setHours(end.getHours() + 1);
      return formatTimeForInput(end);
    }
    return '10:00'; // Default to 10 AM
  };
  
  const formatDateInput = (dateVal) => {
    if (!dateVal) return '';
    const d = new Date(dateVal);
    if (isNaN(d)) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const [formData, setFormData] = useState({
    title: event?.title || '',
    eventType: event?.extendedProps?.eventType || calendars?.[0]?.id || 'meeting',
    location: event?.extendedProps?.location || '',
    notes: event?.extendedProps?.notes || '',
    allDay: event?.allDay || false,
    startDate: formatDateInput(event?.start) || formatDateInput(new Date()),
    endDate: formatDateInput(event?.end) || formatDateInput(new Date()),
    startTime: getDefaultStartTime(),
    endTime: getDefaultEndTime()
  });
  
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setFormData({
      title: event?.title || '',
      eventType: event?.extendedProps?.eventType || calendars?.[0]?.id || 'meeting',
      location: event?.extendedProps?.location || '',
      notes: event?.extendedProps?.notes || '',
      allDay: event?.allDay || false,
      startDate: formatDateInput(event?.start) || formatDateInput(new Date()),
      endDate: formatDateInput(event?.end) || formatDateInput(new Date()),
      startTime: getDefaultStartTime(event),
      endTime: getDefaultEndTime(event)
    });
  }, [event, calendars]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Simple validation
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.eventType) newErrors.eventType = 'Event type is required';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    onSave(formData);
  };

  const eventTypeOptions = calendars && calendars.length
    ? calendars.map(c => ({ id: c.id, text: c.label, color: c.color }))
    : [
        { id: 'shoot', text: 'Shoot', color: theme.palette.success.main },
        { id: 'meeting', text: 'Meeting', color: theme.palette.primary.main },
        { id: 'rehearsal', text: 'Rehearsal', color: theme.palette.secondary.main },
        { id: 'deadline', text: 'Deadline', color: theme.palette.error.main },
        { id: 'preparation', text: 'Preparation', color: theme.palette.warning.main },
        { id: 'review', text: 'Review', color: theme.palette.info.main },
        { id: 'other', text: 'Other', color: theme.palette.grey[500] },
      ];

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
      <Typography variant="h6" component="h2" sx={{ mb: 3 }}>
        {event?.id ? 'Edit Event' : 'Create Event'}
      </Typography>
      
      <TextField
        fullWidth
        margin="normal"
        label="Event Title"
        name="title"
        value={formData.title}
        onChange={handleChange}
        error={!!errors.title}
        helperText={errors.title}
      />
      
      <FormControl fullWidth margin="normal" error={!!errors.eventType}>
        <InputLabel>Event Type</InputLabel>
        <Select
          name="eventType"
          value={formData.eventType}
          onChange={handleChange}
          label="Event Type"
        >
          {eventTypeOptions.map(option => (
            <MenuItem key={option.id} value={option.id}>
              <Box display="flex" alignItems="center">
                <Box 
                  width={12} 
                  height={12} 
                  bgcolor={option.color} 
                  borderRadius="50%"
                  mr={1}
                />
                {option.text}
              </Box>
            </MenuItem>
          ))}
        </Select>
        {errors.eventType && <FormHelperText>{errors.eventType}</FormHelperText>}
      </FormControl>
      
      <TextField
        fullWidth
        margin="normal"
        label="Location"
        name="location"
        value={formData.location}
        onChange={handleChange}
      />
      
      <TextField
        fullWidth
        margin="normal"
        label="Notes"
        name="notes"
        value={formData.notes}
        onChange={handleChange}
        multiline
        rows={4}
      />
      
      <FormControlLabel
        control={
          <Checkbox 
            name="allDay"
            checked={formData.allDay}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              allDay: e.target.checked
            }))}
          />
        }
        label="All Day Event"
        sx={{ mt: 1, mb: 2 }}
      />
      
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          fullWidth
          margin="normal"
          label="Start Date"
          name="startDate"
          type="date"
          value={formData.startDate}
          onChange={handleChange}
          InputLabelProps={{
            shrink: true,
            sx: {
              color: theme.palette.mode === 'dark' ? alpha('#fff', 0.8) : undefined
            }
          }}
          sx={{
            minWidth: 180,
            '& input': { color: theme.palette.mode === 'dark' ? '#fff' : undefined },
            '& input::-webkit-calendar-picker-indicator': {
              filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none'
            }
          }}
        />
        <TextField
          fullWidth
          margin="normal"
          label="End Date"
          name="endDate"
          type="date"
          value={formData.endDate}
          onChange={handleChange}
          InputLabelProps={{
            shrink: true,
            sx: {
              color: theme.palette.mode === 'dark' ? alpha('#fff', 0.8) : undefined
            }
          }}
          sx={{
            minWidth: 180,
            '& input': { color: theme.palette.mode === 'dark' ? '#fff' : undefined },
            '& input::-webkit-calendar-picker-indicator': {
              filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none'
            }
          }}
        />
      </Box>

      {!formData.allDay && (
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            margin="normal"
            label="Start Time"
            name="startTime"
            type="time"
            value={formData.startTime}
            onChange={handleChange}
            InputLabelProps={{
              shrink: true,
              sx: {
                color: theme.palette.mode === 'dark' ? alpha('#fff', 0.8) : undefined
              }
            }}
            inputProps={{
              step: 300, // 5 min intervals
            }}
            sx={{
              '& .MuiInputBase-input': {
                color: theme.palette.mode === 'dark' ? '#fff' : undefined
              },
              '& input': {
                color: theme.palette.mode === 'dark' ? '#fff' : undefined
              },
              '& input::-webkit-calendar-picker-indicator': {
                filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none'
              }
            }}
            InputProps={{
              sx: {
                color: theme.palette.mode === 'dark' ? '#fff' : undefined
              }
            }}
          />
          <TextField
            fullWidth
            margin="normal"
            label="End Time"
            name="endTime"
            type="time"
            value={formData.endTime}
            onChange={handleChange}
            InputLabelProps={{
              shrink: true,
              sx: {
                color: theme.palette.mode === 'dark' ? alpha('#fff', 0.8) : undefined
              }
            }}
            inputProps={{
              step: 300, // 5 min intervals
            }}
            sx={{
              '& .MuiInputBase-input': {
                color: theme.palette.mode === 'dark' ? '#fff' : undefined
              },
              '& input': {
                color: theme.palette.mode === 'dark' ? '#fff' : undefined
              },
              '& input::-webkit-calendar-picker-indicator': {
                filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none'
              }
            }}
            InputProps={{
              sx: {
                color: theme.palette.mode === 'dark' ? '#fff' : undefined
              }
            }}
          />
        </Box>
      )}
      
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        {onDelete && (
          <Button 
            onClick={onDelete}
            color="error"
            variant="outlined"
          >
            Delete
          </Button>
        )}
        <Button 
          onClick={onCancel}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button 
          type="submit"
          variant="contained"
        >
          Save
        </Button>
      </Box>
    </Box>
  );
};

export default function FullProjectCalendar({ projectId, crewMembers: crewMembersProp = [], projectOwnerId = null }) {
  const theme = useTheme();
  const calendarRef = useRef(null);
  const [showForm, setShowForm] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [events, setEvents] = useState([]);
  const [viewTitle, setViewTitle] = useState('');
  const [sidebarDate, setSidebarDate] = useState(new Date());
  const [filters, setFilters] = useState({
    shoot: true,
    meeting: true,
    rehearsal: true,
    deadline: true,
    preparation: true,
    review: true,
    other: true
  });
  const [calendars, setCalendars] = useState([
    { id: 'shoot', label: 'Shoot', color: '#30D158' },
    { id: 'meeting', label: 'Meeting', color: '#0A84FF' },
    { id: 'rehearsal', label: 'Rehearsal', color: '#BF5AF2' },
    { id: 'deadline', label: 'Deadline', color: '#FF453A' },
    { id: 'preparation', label: 'Preparation', color: '#FF9F0A' },
    { id: 'review', label: 'Review', color: '#64D2FF' },
    { id: 'other', label: 'Other', color: '#98989D' }
  ]);
  const [newCalendarLabel, setNewCalendarLabel] = useState('');
  const [canViewCalendar, setCanViewCalendar] = useState(false);
  const [canEditCalendar, setCanEditCalendar] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const headers = {
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json'
  };
  
  console.log('Initializing FullCalendar with projectId:', projectId);
  console.log('Component mounted, calendar ref:', calendarRef.current);
  if (calendarRef.current) {
    console.log('Calendar API available:', !!calendarRef.current.getApi);
  }

  console.log('Available plugins:', {
    dayGrid: !!dayGridPlugin,
    timeGrid: !!timeGridPlugin,
    interaction: !!interactionPlugin
  });

  const fetchEvents = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        enqueueSnackbar('Authentication required', { variant: 'error' });
        return;
      }

      const response = await axios.get(`/api/v1/events/project/${projectId}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-auth-token': token
        },
        withCredentials: true
      });

      setEvents(response.data.map(event => {
        const start = new Date(event.startDate);
        const end = new Date(event.endDate);
        const persistedAllDay = event.allDay ?? event.isAllDay;
        const derivedAllDay = persistedAllDay !== undefined
          ? persistedAllDay
          : (start.getHours() === 0 && start.getMinutes() === 0 && end.getHours() === 23 && end.getMinutes() === 59);
        return {
          id: event._id,
          title: event.title,
          start,
          end,
          allDay: derivedAllDay,
          extendedProps: {
            notes: event.notes,
            eventType: event.eventType,
            location: event.location,
            allDay: derivedAllDay
          }
        };
      }));
    } catch (error) {
      console.error('Error fetching events:', error);
      if (error.response?.status === 403) {
        enqueueSnackbar('You do not have access to this project calendar', { variant: 'warning' });
      } else {
        enqueueSnackbar('Failed to load events', { variant: 'error' });
      }
    }
  }, [projectId, enqueueSnackbar]);

  // Determine permissions for current user based on passed crew/owner info, fallback to project fetch
  useEffect(() => {
    const initPermissions = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        // get user id from JWT
        let userId = null;
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          userId = payload.id || payload._id || payload.userId || payload.sub || null;
        } catch (_) {}

        // Prefer props to compute permissions quickly
        const propOwnerId = typeof projectOwnerId === 'object' ? projectOwnerId?._id : projectOwnerId;
        const isOwnerByProps = propOwnerId && userId && (propOwnerId === userId);
        const inCrewByProps = Array.isArray(crewMembersProp) && userId && crewMembersProp.some(m => {
          // shapes: { user: {_id,...} } or string id or {_id: userId}
          if (!m) return false;
          if (typeof m === 'string') return m === userId;
          if (m.user && (m.user._id || m.user.id)) return (m.user._id || m.user.id) === userId;
          if (m._id || m.id) return (m._id || m.id) === userId;
          return false;
        });

        if (isOwnerByProps || inCrewByProps) {
          setCanViewCalendar(true);
          setCanEditCalendar(true);
          await fetchEvents();
          return;
        }

        // Fallback: fetch project details in case watchers or other membership mechanisms grant view
        const projResp = await axios.get(`/api/v1/projects/${projectId}`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'x-auth-token': token
          }
        });
        const project = projResp.data;

        const isOwner = (project?.owner && ((typeof project.owner === 'string' ? project.owner : project.owner?._id) === userId)) || isOwnerByProps;
        const inCrew = Array.isArray(project?.crewMembers) && project.crewMembers.some(cm => (typeof cm === 'string' ? cm : cm?._id) === userId);
        const isWatcher = Array.isArray(project?.watchers) && project.watchers.some(w => (typeof w === 'string' ? w : w?._id) === userId);
        // Policy: owner + crew + watchers can view; owner + crew can edit
        const canView = !!(isOwner || inCrew || isWatcher);
        const canEdit = !!(isOwner || inCrew);
        setCanViewCalendar(canView);
        setCanEditCalendar(canEdit);
        if (canView) {
          fetchEvents();
        }
      } catch (e) {
        console.error('Failed to determine calendar permissions:', e);
        // Fallback: try to fetch events; backend will enforce authorization
        fetchEvents();
      }
    };
    initPermissions();
  }, [projectId, projectOwnerId, JSON.stringify(crewMembersProp), fetchEvents]);

  // Define handlers first
  const handleEventAdd = async (addInfo) => {
    try {
      console.log('Attempting to create event:', addInfo);
      
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        enqueueSnackbar('Please login again', { variant: 'error' });
        addInfo.revert();
        return;
      }

      console.log('Project ID being sent:', projectId);
      console.log('Event data:', {
        title: addInfo.event.title,
        startDate: addInfo.event.start,
        endDate: addInfo.event.end,
        eventType: addInfo.event.extendedProps.eventType || 'other'
      });

      const response = await axios.post('/api/v1/events', {
        title: addInfo.event.title,
        startDate: addInfo.event.start,
        endDate: addInfo.event.end,
        eventType: addInfo.event.extendedProps.eventType || 'other',
        projectId,
        allDay: addInfo.event.allDay
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('API Response:', response.data);
      enqueueSnackbar('Event created successfully', { variant: 'success' });
    } catch (error) {
      console.error('Event creation failed:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      enqueueSnackbar(`Failed to create event: ${error.response?.data?.message || error.message}`, { variant: 'error' });
      addInfo.revert();
    }
  };

  const handleDateSelect = (selectInfo) => {
    if (!canEditCalendar) return; // block if no edit permission
    setCurrentEvent({
      start: selectInfo.start,
      end: selectInfo.end,
      allDay: selectInfo.allDay
    });
    setShowForm(true);
  };

  const handleEventUpdate = async (updateInfo) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`/api/v1/events/${updateInfo.event.id}`, {
        title: updateInfo.event.title,
        startDate: updateInfo.event.start,
        endDate: updateInfo.event.end,
        eventType: updateInfo.event.extendedProps.eventType,
        notes: updateInfo.event.extendedProps.notes || '',
        location: updateInfo.event.extendedProps.location || '',
        description: updateInfo.event.extendedProps.notes || '',
        projectId: projectId,
        allDay: updateInfo.event.allDay
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        withCredentials: true
      });
      
      if (response.status >= 400) {
        console.error('API Error Details:', {
          url: response.config.url,
          method: response.config.method,
          payload: response.config.data,
          status: response.status,
          response: response.data || 'No response data'
        });
        throw new Error(response.data?.message || `Failed to update event`);
      }

      enqueueSnackbar('Event updated successfully', { variant: 'success' });
    } catch (error) {
      if (error.response?.status === 404) {
        enqueueSnackbar('Failed to update: project not found or you lack access', { variant: 'error' });
      } else if (error.response?.status === 403) {
        enqueueSnackbar('You do not have permission to edit this event', { variant: 'warning' });
      } else {
        enqueueSnackbar('Failed to update event', { variant: 'error' });
      }
      updateInfo.revert();
    }
  };

  const handleEventResize = async (resizeInfo) => handleEventUpdate(resizeInfo);

  const handleEventClick = (clickInfo) => {
    if (!canViewCalendar) return;
    setCurrentEvent(clickInfo.event);
    setShowForm(true);
  };

  const handleQuickCreate = () => {
    if (!canEditCalendar) return;
    const now = new Date();
    setCurrentEvent({
      start: now,
      end: new Date(now.getTime() + 60 * 60 * 1000),
      allDay: false
    });
    setShowForm(true);
  };

  const handleDateClick = (info) => {
    if (!canEditCalendar) return;
    console.log('Date clicked - current showForm state:', showForm);
    setCurrentEvent({
      start: info.date,
      end: new Date(info.date.getTime() + 60 * 60 * 1000),
      allDay: info.allDay
    });
    console.log('Setting showForm to true');
    setShowForm(true);
  };

  const handleSaveEvent = async (formData) => {
    try {
      // Validate and sanitize inputs
      if (!formData.title || !formData.title.trim()) {
        throw new Error('Event title is required');
      }
      
      const sanitizedTitle = formData.title.trim()
        .replace(/[;'"\/\\]/g, '')
        .substring(0, 100);
      
      if (sanitizedTitle.length < 2) {
        throw new Error('Title must be at least 2 characters');
      }
      
      if (!currentEvent?.start || !currentEvent?.end) {
        throw new Error('Event must have start and end times');
      }

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }
      
      let startDate = formData.startDate ? new Date(formData.startDate) : new Date(currentEvent.start);
      let endDate = formData.endDate ? new Date(formData.endDate) : new Date(currentEvent.end);
      
      // Apply time from form if not all-day event
      if (!formData.allDay) {
        if (formData.startTime) {
          const [startHours, startMinutes] = formData.startTime.split(':').map(Number);
          if (!isNaN(startHours) && !isNaN(startMinutes)) {
            startDate.setHours(startHours, startMinutes, 0, 0);
          }
        } else {
          // Default to 9:00 AM if no time specified
          startDate.setHours(9, 0, 0, 0);
        }
        
        if (formData.endTime) {
          const [endHours, endMinutes] = formData.endTime.split(':').map(Number);
          if (!isNaN(endHours) && !isNaN(endMinutes)) {
            endDate.setHours(endHours, endMinutes, 0, 0);
          }
        } else {
          // Default to 1 hour after start if no end time specified
          endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
        }
      } else {
        // For all-day events, set to start of day
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
      }
      
      // Validate that end is after start
      if (endDate <= startDate) {
        throw new Error('End time must be after start time');
      }
      
      // Determine if we're creating or updating
      const isUpdate = currentEvent?.id;
      
      const allowedTypes = calendars.map(c => c.id);
      const safeEventType = allowedTypes.includes(formData.eventType) ? formData.eventType : (allowedTypes[0] || 'other');

      const eventData = {
        title: sanitizedTitle,
        description: formData.notes || '',
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        eventType: safeEventType,
        location: formData.location || '',
        participants: [],
        notes: formData.notes || '',
        projectId: projectId,
        allDay: formData.allDay
      };

      const response = isUpdate
        ? await axios.put(`/api/v1/events/${currentEvent.id}`, eventData, {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
              'x-auth-token': token
            },
            withCredentials: true,
            validateStatus: (status) => true
          })
        : await axios.post('/api/v1/events', eventData, {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
              'x-auth-token': token
            },
            withCredentials: true,
            validateStatus: (status) => true
          });

      if (response.status >= 400) {
        console.error('API Error Details:', {
          url: response.config.url,
          method: response.config.method,
          payload: response.config.data,
          status: response.status,
          response: response.data || 'No response data'
        });
        throw new Error(response.data?.message || `Failed to ${isUpdate ? 'update' : 'create'} event`);
      }

      fetchEvents(); // Refresh events
      setShowForm(false);
      setCurrentEvent(null);
      enqueueSnackbar(`Event ${isUpdate ? 'updated' : 'created'} successfully`, { variant: 'success' });
    } catch (error) {
      console.error('Error saving event:', error);
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`/api/v1/events/${eventId}?projectId=${projectId}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-auth-token': token
        },
        withCredentials: true,
        validateStatus: (status) => true
      });

      if (response.status >= 400) {
        throw new Error(response.data.message || 'Failed to delete event');
      }

      setShowForm(false); // Close modal immediately
      setCurrentEvent(null);
      fetchEvents(); // Refresh events
      enqueueSnackbar('Event deleted successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error deleting event:', error);
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  // Then define calendarOptions
  const renderEventContent = (eventInfo) => {
    const eventColor = getEventColor(eventInfo.event.extendedProps.eventType, theme);
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: '4px 8px',
          borderRadius: '6px',
          backgroundColor: eventColor,
          color: '#fff',
          boxShadow: theme.palette.mode === 'dark' ? '0 2px 6px rgba(0,0,0,0.35)' : '0 1px 4px rgba(0,0,0,0.12)',
          transition: 'transform 0.15s ease, box-shadow 0.15s ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: theme.palette.mode === 'dark' ? '0 3px 10px rgba(0,0,0,0.45)' : '0 2px 8px rgba(0,0,0,0.18)'
          }
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.25, color: '#fff' }}>
          {eventInfo.timeText && (
            <span style={{ marginRight: 6, opacity: 0.9 }}>{eventInfo.timeText}</span>
          )}
          {eventInfo.event.title}
        </Typography>
        {eventInfo.event.extendedProps.location && (
          <Typography variant="caption" sx={{ color: alpha('#fff', 0.9), display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {eventInfo.event.extendedProps.location}
          </Typography>
        )}
      </Box>
    );
  };

  const calendarOptions = {
    headerToolbar: false,
    themeSystem: 'standard',
    height: '820px',
    aspectRatio: 1.8,
    expandRows: true,
    navLinks: true,
    nowIndicator: true,
    dayMaxEvents: true,
    eventDisplay: 'block',
    eventTimeFormat: {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    },
    eventDidMount: (info) => {
      // Add tooltip for events
      if (info.event.extendedProps.notes) {
        info.el.setAttribute('title', info.event.extendedProps.notes);
      }
    },
    // Modern header styling
    // Professional day headers
    dayHeaderContent: (args) => {
      return {
        html: `<div style="
          color: ${theme.palette.text.primary};
          font-weight: 600;
          font-size: 0.9rem;
          letter-spacing: 0.5px;
          text-transform: uppercase;
          padding: 8px 0;
        ">${args.text}</div>`
      };
    },
    // Elegant day cells
    dayCellContent: (args) => {
      const isWeekend = [0, 6].includes(args.date.getDay());
      return {
        html: `<div style="
          color: ${args.isToday 
            ? theme.palette.primary.main 
            : isWeekend 
              ? theme.palette.text.secondary 
              : theme.palette.text.primary};
          font-weight: ${args.isToday ? '600' : '400'};
          margin: 4px;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          background: ${args.isToday ? alpha(theme.palette.primary.main, 0.1) : 'transparent'};
        ">${args.dayNumberText}</div>`
      };
    },
    // Modern event styling
    eventContent: renderEventContent,
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
    initialView: 'dayGridMonth',
    selectable: canEditCalendar,
    select: handleDateSelect,
    eventAdd: handleEventAdd,
    editable: canEditCalendar,
    eventStartEditable: canEditCalendar,
    eventDurationEditable: canEditCalendar,
    eventResizableFromStart: canEditCalendar,
    eventChange: handleEventUpdate,
    eventResize: handleEventResize,
    eventClick: handleEventClick,
    dateClick: handleDateClick,
    selectMirror: true,
    selectMinDistance: 5, // pixels
    selectLongPressDelay: 100,
    events: events.filter(e => filters[e.extendedProps.eventType] !== false),
    datesSet: (arg) => {
      setViewTitle(arg.view.title);
      setSidebarDate(arg.view.currentStart);
    },
    dayCellClassNames: (arg) => {
      if (arg.isToday) {
        return theme.palette.mode === 'dark' 
          ? 'fc-day-today-dark' 
          : 'fc-day-today-light';
      }
      return '';
    },
  };

  const miniMonth = useMemo(() => {
    const year = sidebarDate.getFullYear();
    const month = sidebarDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDay = firstDay.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [sidebarDate]);

  return (
    <>
      {showForm && canViewCalendar && (
        <Dialog 
          open={showForm}
          onClose={() => setShowForm(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            style: {
              borderRadius: 12,
              padding: theme.spacing(3),
              backgroundColor: theme.palette.background.paper
            }
          }}
          BackdropProps={{
            style: {
              backgroundColor: alpha(theme.palette.background.default, 0.8)
            }
          }}
        >
          <EventForm 
            event={currentEvent} 
            onSave={handleSaveEvent} 
            onCancel={() => setShowForm(false)}
            onDelete={currentEvent?.id ? () => handleDeleteEvent(currentEvent.id) : null}
            calendars={calendars}
          />
        </Dialog>
      )}
      
      <style>{`
        .fc-day-today-dark {
          background-color: ${alpha(theme.palette.primary.main, 0.15)} !important;
        }
        .fc-day-today-light {
          background-color: ${alpha(theme.palette.primary.light, 0.2)} !important;
        }
        .fc-theme-standard td, .fc-theme-standard th {
          border-color: ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.06 : 0.15)} !important;
          border-width: 0.5px;
        }
        .fc-scrollgrid, .fc-theme-standard .fc-scrollgrid {
          background-color: ${theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.85) : '#fff'};
          border: none;
        }
        .fc-scrollgrid table {
          border: none !important;
        }
        .fc-col-header-cell {
          background-color: ${theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.9) : alpha('#f7f9fb', 0.9)};
          color: ${theme.palette.text.primary};
          border-color: ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.1 : 0.15)} !important;
        }
        .fc-timegrid-axis-frame, .fc-timegrid-slot-label-frame {
          color: ${alpha(theme.palette.text.secondary, 0.9)};
          font-weight: 500;
        }
      `}</style>
      
      <Paper 
        elevation={0} 
        sx={{
          mb: 2, 
          p: { xs: 2, md: 3 },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          backgroundColor: theme.palette.background.default,
          color: theme.palette.mode === 'dark' ? 'white' : theme.palette.text.primary,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
          boxShadow: theme.palette.mode === 'dark'
            ? '0 10px 30px rgba(0,0,0,0.35)'
            : '0 8px 24px rgba(0,0,0,0.08)'
        }}
      >
        {/* Sidebar */}
        <Box sx={{ width: { xs: '100%', md: 260 }, borderRight: { md: `1px solid ${alpha(theme.palette.divider, 0.2)}` }, pr: { md: 2 } }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <Button variant="outlined" size="small" onClick={() => calendarRef.current?.getApi()?.today()}>
              Today
            </Button>
            <Button variant="contained" size="small" onClick={handleQuickCreate}>
              Create Event
            </Button>
          </Stack>

          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              {sidebarDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="text" onClick={() => {
                const api = calendarRef.current?.getApi();
                api?.prev();
                setSidebarDate(api?.getDate() || new Date());
              }}>◀</Button>
              <Button size="small" variant="text" onClick={() => {
                const api = calendarRef.current?.getApi();
                api?.next();
                setSidebarDate(api?.getDate() || new Date());
              }}>▶</Button>
            </Stack>
          </Stack>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 2 }}>
            {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
              <Typography key={d} variant="caption" align="center" color="text.secondary">{d}</Typography>
            ))}
            {miniMonth.map((day, idx) => {
              const isToday = day === new Date().getDate() && sidebarDate.getMonth() === new Date().getMonth() && sidebarDate.getFullYear() === new Date().getFullYear();
              return (
                <Box key={idx} onClick={() => {
                  if (!day) return;
                  const target = new Date(sidebarDate.getFullYear(), sidebarDate.getMonth(), day);
                  calendarRef.current?.getApi()?.gotoDate(target);
                  setSidebarDate(target);
                }} sx={{
                  height: 30,
                  borderRadius: 1,
                  bgcolor: isToday ? alpha(theme.palette.primary.main, 0.15) : 'transparent',
                  color: day ? theme.palette.text.primary : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: day ? 'pointer' : 'default',
                  '&:hover': day ? { backgroundColor: alpha(theme.palette.primary.main, 0.1) } : {}
                }}>
                  <Typography variant="caption">{day || ''}</Typography>
                </Box>
              );
            })}
          </Box>

          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>My Calendars</Typography>
          <FormGroup>
            {calendars.map((item) => (
              <FormControlLabel
                key={item.id}
                control={<Checkbox size="small" checked={!!filters[item.id]} onChange={(e) => setFilters(prev => ({ ...prev, [item.id]: e.target.checked }))} />}
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: item.color }} />
                    <Typography variant="body2">{item.label}</Typography>
                    {['shoot','meeting','rehearsal','deadline','preparation','review','other'].includes(item.id) ? null : (
                      <IconButton size="small" onClick={() => {
                        setCalendars(prev => prev.filter(c => c.id !== item.id));
                        setFilters(prev => {
                          const n = { ...prev };
                          delete n[item.id];
                          return n;
                        });
                      }}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                }
              />
            ))}
          </FormGroup>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
            <TextField
              size="small"
              variant="outlined"
              placeholder="Add calendar"
              value={newCalendarLabel}
              onChange={(e) => setNewCalendarLabel(e.target.value)}
              fullWidth
            />
            <IconButton
              color="primary"
              onClick={() => {
                const label = newCalendarLabel.trim();
                if (!label) return;
                const id = label.toLowerCase().replace(/\s+/g, '-');
                if (calendars.some(c => c.id === id)) {
                  setNewCalendarLabel('');
                  return;
                }
                const palette = ['#30D158','#0A84FF','#BF5AF2','#FF453A','#FF9F0A','#64D2FF','#98989D'];
                const color = palette[calendars.length % palette.length];
                setCalendars(prev => [...prev, { id, label, color }]);
                setFilters(prev => ({ ...prev, [id]: true }));
                setNewCalendarLabel('');
              }}
            >
              <AddCircleOutline />
            </IconButton>
          </Stack>
        </Box>

        {/* Main Calendar */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button variant="outlined" size="small" onClick={() => calendarRef.current?.getApi()?.prev()}>Prev</Button>
              <Button variant="outlined" size="small" onClick={() => calendarRef.current?.getApi()?.today()}>Today</Button>
              <Button variant="outlined" size="small" onClick={() => calendarRef.current?.getApi()?.next()}>Next</Button>
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              <Typography variant="subtitle1" fontWeight={700}>{viewTitle || 'Calendar'}</Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="outlined" onClick={() => calendarRef.current?.getApi()?.changeView('dayGridMonth')}>Month</Button>
              <Button size="small" variant="outlined" onClick={() => calendarRef.current?.getApi()?.changeView('timeGridWeek')}>Week</Button>
              <Button size="small" variant="outlined" onClick={() => calendarRef.current?.getApi()?.changeView('timeGridDay')}>Day</Button>
            </Stack>
          </Stack>

          {canViewCalendar ? (
            <FullCalendar 
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              {...calendarOptions}
            />
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                You do not have access to view this project calendar.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please ask the project owner to add you as a crew member or watcher.
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
    </>
  );
}

function getEventColor(eventType, theme) {
  const isDark = theme.palette.mode === 'dark';
  const colors = {
    shoot: isDark ? '#30D158' : '#34C759',
    meeting: isDark ? '#0A84FF' : '#007AFF',
    rehearsal: isDark ? '#BF5AF2' : '#AF52DE',
    deadline: isDark ? '#FF453A' : '#FF3B30',
    preparation: isDark ? '#FF9F0A' : '#FF9500',
    review: isDark ? '#64D2FF' : '#5AC8FA',
    other: isDark ? '#98989D' : '#8E8E93'
  };
  return colors[eventType] || colors.other;
}
